import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import type { SiteDocDetail } from '@symphenia/shared-types'
import { frage } from '@/lib/dialoge'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDate, fmtDateTime } from '@/lib/format-date'
import { resendSiteDocConfirmation } from '../api/site-docs.api'

/**
 * AP-46 22.2 — **Stand der Bestätigung per Link** am Detail: an wen, gesendet, geöffnet, Erinnerungen, gültig
 * bis bzw. abgelaufen; „Neu senden" an dieselbe oder eine andere Adresse (das alte Token gilt dann nicht mehr).
 */
export function BestaetigungStand({ dok, darfSenden, onGeaendert }: { dok: SiteDocDetail; darfSenden: boolean; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const [fehler, setFehler] = useState<string | null>(null)
  const b = dok.confirmation
  if (!b) return null
  const zeile = (label: string, wert: string | null) =>
    wert ? (
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">{label}</dt>
        <dd className="text-right">{wert}</dd>
      </div>
    ) : null

  const neuSenden = async () => {
    const email = await frage(t('siteDocs.confirmation.resendPrompt'), b.email, { pflicht: true, ok: t('siteDocs.confirmation.resend') })
    if (!email?.trim()) return
    setFehler(null)
    try {
      onGeaendert(await resendSiteDocConfirmation(dok.id, { email: email.trim(), name: email.trim() === b.email ? b.name : null }))
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  return (
    <div className="space-y-2 text-sm">
      <h2 className="font-medium">{t('siteDocs.confirmation.title')}</h2>
      <dl className="space-y-1">
        {zeile(t('siteDocs.confirmation.to'), b.name ? `${b.name} · ${b.email}` : b.email)}
        {zeile(t('siteDocs.confirmation.sent'), b.sentAt ? fmtDateTime(b.sentAt) : t('siteDocs.confirmation.notYet'))}
        {zeile(t('siteDocs.confirmation.opened'), b.openedAt ? fmtDateTime(b.openedAt) : null)}
        {zeile(t('siteDocs.confirmation.reminded'), b.remindedAt.length ? b.remindedAt.map((d) => fmtDate(d)).join(', ') : null)}
        {zeile(t('siteDocs.confirmation.answered'), b.answeredAt ? fmtDateTime(b.answeredAt) : null)}
        {!b.answeredAt && zeile(b.expired ? t('siteDocs.confirmation.expired') : t('siteDocs.confirmation.validUntil'), fmtDate(b.expiresAt))}
      </dl>
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
      {darfSenden && dok.status === 'AWAITING' && (
        <button type="button" onClick={() => void neuSenden()} className="inline-flex h-9 items-center gap-1 rounded-md border px-3 text-sm">
          <Send className="h-4 w-4" /> {t('siteDocs.confirmation.resend')}
        </button>
      )}
    </div>
  )
}
